import { Box, Collapse, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  TreeItemCheckbox,
  TreeItemContent,
  TreeItemIcon,
  TreeItemIconContainer,
  TreeItemLabel,
  TreeItemProvider,
  TreeItemRoot,
  useTreeItem,
  useTreeItemModel,
  UseTreeItemParameters,
} from "@mui/x-tree-view-pro";
import { MdsArrowRightAlt } from "@sinch/icons";
import { omit } from "ramda";
import React from "react";
import { MdsIcon } from "@sinch/components";
import { RatelistNameFragment } from "@sinch/components/application/Rate/RateMigration/queries.types";
import { ExtendedTreeItemProps } from "@sinch/components/application/Rate/RateMigration/types";
import { useRateRevision } from "@sinch/components/application/Rate/tools";

export interface CustomTreeItemProps
  extends Omit<UseTreeItemParameters, "rootRef">,
    Omit<React.HTMLAttributes<HTMLLIElement>, "onFocus"> {
  targetRatelist?: RatelistNameFragment;
}

const RateMigrationRowComponent = (props: CustomTreeItemProps, ref: React.Ref<HTMLLIElement>) => {
  const { id, itemId, label, disabled, children, targetRatelist, ...other } = props;
  const { getRevisionNumber } = useRateRevision();

  const {
    getContextProviderProps,
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getCheckboxProps,
    getLabelProps,
    getGroupTransitionProps,
    status,
  } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });

  const item = useTreeItemModel<ExtendedTreeItemProps>(itemId)!;
  const canMigrate = item.canMigrate;
  const rootProps = omit(canMigrate ? ["onBlur", "onFocus", "onKeyDown"] : [], getRootProps(other));

  return (
    <TreeItemProvider {...getContextProviderProps()}>
      <TreeItemRoot {...rootProps}>
        <TreeItemContent {...getContentProps()}>
          {canMigrate && <TreeItemCheckbox {...getCheckboxProps()} disabled={!item.selectable} />}
          <TreeItemIconContainer {...getIconContainerProps()}>
            {canMigrate && status.expandable && <TreeItemIcon status={status} />}
          </TreeItemIconContainer>
          <LabelContainer {...getLabelProps()}>
            <Row alignItems="center" direction="row" justifyContent="space-between" spacing={3}>
              <span>{item.renderLabel}</span>
              {canMigrate && item.ratelist && (
                <Stack alignItems="center" direction="row" spacing={1}>
                  <RatelistText dimmed={status.selected}>
                    {item.ratelist.name}{" "}
                    <Typography sx={{ color: "inherit" }} variant="descriptionSemi">
                      {getRevisionNumber(item.ratelist.version)}
                    </Typography>
                  </RatelistText>
                  {status.selected && targetRatelist && (
                    <>
                      <MdsIcon icon={MdsArrowRightAlt} />
                      <TargetText>
                        {targetRatelist.name} {getRevisionNumber(targetRatelist.version)}
                      </TargetText>
                    </>
                  )}
                </Stack>
              )}
            </Row>
          </LabelContainer>
        </TreeItemContent>
        {children && <GroupCollapse {...getGroupTransitionProps(!canMigrate ? { in: true } : {})} />}
      </TreeItemRoot>
    </TreeItemProvider>
  );
};

export const RateMigrationRow = React.memo(React.forwardRef(RateMigrationRowComponent));

const LabelContainer = styled(TreeItemLabel)({
  display: "flex",
  alignItems: "center",
});

const Row = styled(Stack)({
  width: "100%",
  minHeight: 32,
});

const RatelistText = styled(Box, {
  shouldForwardProp: (prop) => prop !== "dimmed",
})<{ dimmed?: boolean }>(({ theme, dimmed }) => ({
  color: dimmed ? theme.palette.grey[500] : undefined,
}));

const TargetText = styled(Typography)({
  fontWeight: 600,
});

const GroupCollapse = styled(Collapse)({
  paddingInlineStart: 8,
});
